import "video.js/dist/video-js.css";
import { useCallback, useRef, useState } from "react";
import { ArrowsPointingInIcon } from "@heroicons/react/20/solid";
import { ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import {
  Cog6ToothIcon,
  DocumentTextIcon,
  PauseIcon,
  PlayIcon,
} from "@heroicons/react/24/solid";
import { PlaybackRateMenu } from "./components/PlaybackRateMenu";
import { QualityBadge } from "./components/QualityBadge";
import { SettingsPopover } from "./components/SettingsPopover";
import { TimeDisplay } from "./components/TimeDisplay";
import { TimelineScrubber } from "./components/TimelineScrubber";
import { TranscriptPanel } from "./components/TranscriptPanel";
import { VideoSurface } from "./components/VideoSurface";
import { VolumeControl } from "./components/VolumeControl";
import type {
  Transcription,
  TranscriptionStatus,
} from "./lib/transcription";
import { useHotkeys } from "./hooks/useHotkeys";
import { usePlayer, type VideoJsPlayer } from "./hooks/usePlayer";
import { type TimeFormat } from "./lib/timecode";
import {
  useDocumentIsFullScreen,
  useMediaQuery,
  useResizeObserver,
} from "./hooks";
import { useSettingsState } from "./settingsStore";
import {
  classNames,
  getUUID,
  toggleElementFullScreen,
} from "./utils";

// Carry-over from the legacy player. HTMLVideoElement doesn't expose
// frame rate directly; the only browser-side path is timing consecutive
// frames via requestVideoFrameCallback, which isn't worth it for a
// single fixed-fps clip. Revisit if a variable-fps source ever lands.
const getFrameRate = () => 30;

export interface VideoPlayerProps {
  src: string;
  type?: string;
  proxySrc?: string | null;
  proxyType?: string;
  /** Optional transcript. If provided, renders the transcript panel beside the video. */
  transcription?: Transcription | null;
  /** Lifecycle of the transcript load. Controls whether the panel shows loading / error / data. */
  transcriptionStatus?: TranscriptionStatus;
  /** Called when the user clicks Retry on the error state. */
  onRetryTranscription?: () => void;
  onReady?: (player: VideoJsPlayer) => void;
  onOpenError?: (e: unknown) => void;
  disableHotkeys?: boolean;
  alwaysAllowHotkeys?: boolean;
  disableZoom?: boolean;
  disableFullScreen?: boolean;
  hideControls?: boolean;
  disableInitialFocus?: boolean;
  getContainer?: () => HTMLElement;
}

const VideoPlayer = ({
  src,
  type = "video/mp4",
  proxySrc,
  proxyType = "video/mp4",
  transcription = null,
  transcriptionStatus,
  onRetryTranscription,
  onReady,
  onOpenError,
  disableHotkeys,
  alwaysAllowHotkeys,
  disableZoom,
  disableFullScreen,
  hideControls,
  disableInitialFocus,
  getContainer: getContainerFromProps,
}: VideoPlayerProps) => {
  const {
    videoPlayerVolume: persistedVolume,
    setVideoPlayerVolume: setPersistedVolume,
    videoPlayerMuted: persistedMuted,
    setVideoPlayerMuted: setPersistedMuted,
    videoPlayerPlaybackRate: persistedRate,
    setVideoPlayerPlaybackRate: setPersistedRate,
    preferredTimeFormat,
    setPreferredTimeFormat,
    videoBackgroundWhite,
    transcriptPanelOpen,
    setTranscriptPanelOpen,
  } = useSettingsState();

  // Quality switching — preserved from legacy. Defaults to Original; switches
  // via setSrc which preserves currentTime + play state across the swap.
  const [currentQuality, setCurrentQuality] = useState<"1080p" | "Original">(
    "Original",
  );
  const canSwitchQuality = !!(proxySrc && src);
  const activeSrc = currentQuality === "1080p" && proxySrc ? proxySrc : src;
  const activeType =
    currentQuality === "1080p" && proxySrc ? proxyType : type;

  const player = usePlayer({
    src: activeSrc,
    type: activeType,
    initialVolume: persistedVolume,
    initialMuted: persistedMuted,
    initialPlaybackRate: persistedRate,
    disableInitialFocus,
    onReady,
    onError: onOpenError,
    onVolumeChange: (volume, muted) => {
      setPersistedVolume(volume);
      setPersistedMuted(muted);
    },
    onPlaybackRateChange: setPersistedRate,
  });

  // ---- Layout / viewport state ----
  const [videoPlayerRef, videoPlayerDims] = useResizeObserver();
  const isSmallScreen = useMediaQuery("(max-width: 767px)");
  const isFullScreen = useDocumentIsFullScreen();
  const isCompactVideoControls = (videoPlayerDims?.width ?? 0) < 700;

  // ---- UI state ----
  const [centerButtonPing, setCenterButtonPing] = useState(false);
  const localContainerRef = useRef<HTMLDivElement | null>(null);

  const [id] = useState(getUUID());
  const timelineId = `timeline-${id}`;
  const controlsId = `controls-${id}`;

  const timeFormat: TimeFormat = preferredTimeFormat ?? "standard";

  const getContainer = () =>
    getContainerFromProps?.() || localContainerRef.current;
  const toggleFullScreen = () => toggleElementFullScreen(getContainer()!);
  const pingCenterButton = () => setCenterButtonPing((p) => !p);

  // Click / dblclick on the video surface — orchestrator decides what to do
  // because it owns the controls-bar identity check (skip toggle if the
  // click landed on a control). Memoized so VideoSurface's listener effect
  // doesn't re-run on every render.
  const handleVideoClick = useCallback(
    (e: MouseEvent) => {
      const container = document.getElementById(controlsId);
      if (!container || !container.contains(e.target as Node | null)) {
        player.togglePlay();
      }
    },
    [controlsId, player.togglePlay],
  );
  const handleVideoDblClick = useCallback(
    (e: MouseEvent) => {
      const container = document.getElementById(controlsId);
      if (!container || !container.contains(e.target as Node | null)) {
        toggleFullScreen();
      }
    },
    // toggleFullScreen reads localContainerRef.current via getContainer(),
    // which is stable for the component lifetime — no deps needed for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlsId],
  );

  useHotkeys({
    player,
    containerRef: localContainerRef,
    enabled: !disableHotkeys,
    alwaysAllow: alwaysAllowHotkeys,
    fps: getFrameRate(),
    onFullScreenToggle: toggleFullScreen,
    onScrubFeedback: pingCenterButton,
  });

  // ---- Quality switching ----
  const handleQualityChange = (quality: "1080p" | "Original") => {
    if (quality === currentQuality) return;
    const newSource = quality === "1080p" ? proxySrc : src;
    const newType = quality === "1080p" ? proxyType : type;
    if (!newSource || !newType) return;
    setCurrentQuality(quality);
    player.setSrc(newSource, newType);
  };

  // Panel is available whenever the parent told us *something* about a
  // transcript — including loading/error states, so the user can open the
  // panel and see the error UI + retry.
  const transcriptAvailable = transcriptionStatus !== undefined;
  const showTranscriptPanel = transcriptPanelOpen && transcriptAvailable;

  return (
    <div
      ref={(e) => {
        localContainerRef.current = e;
      }}
      className="flex h-full w-full"
    >
      {showTranscriptPanel && (
        <TranscriptPanel
          transcription={transcription}
          status={transcriptionStatus!}
          onRetry={onRetryTranscription}
          currentTimeSec={player.time}
          onSeek={player.seek}
        />
      )}
      <div
        className={`relative flex h-full min-w-0 flex-1 flex-col justify-center ${videoBackgroundWhite ? "bg-white" : "bg-black"}`}
        tabIndex={0}
        ref={(e) => {
          videoPlayerRef.current = e;
        }}
      >
        <VideoSurface
          videoRef={player.videoRef}
          videoElementNode={player.videoElementNode}
          playerRef={player.playerRef}
          tempPlaybackRateRef={player.tempPlaybackRateRef}
          centerButtonPing={centerButtonPing}
          videoBackgroundWhite={videoBackgroundWhite}
          isSmallScreen={isSmallScreen}
          isFullScreen={isFullScreen}
          disableZoom={disableZoom}
          onVideoClick={handleVideoClick}
          onVideoDblClick={handleVideoDblClick}
        />

        {!hideControls && (
          <div
            id={controlsId}
            className={classNames(
              "p-3 pt-2",
              !isFullScreen && "bg-white pb-6",
            )}
          >
            <TimelineScrubber
              id={timelineId}
              time={player.time}
              duration={player.duration}
              bufferAmount={player.bufferAmount}
              fps={getFrameRate()}
              isPlaying={player.playing}
              onSeek={player.seek}
              onScrubStart={player.pause}
              onScrubEnd={player.play}
            />

            {/*
              Grid (not flex justify-between) so the volume slider
              expanding on hover doesn't shift the centered time display.
              Two equal 1fr side columns sandwich an auto-sized middle.
            */}
            <div className="mt-4 grid w-full grid-cols-[1fr_auto_1fr] items-center px-2 text-[#474C57]">
              <div className="flex items-center gap-x-3">
                <button
                  type="button"
                  onClick={player.togglePlay}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-[#282A30] transition-colors hover:bg-[#F7F8F9] hover:text-black"
                  aria-label={player.playing ? "Pause" : "Play"}
                >
                  {player.playing ? (
                    <PauseIcon className="h-4 w-4" />
                  ) : (
                    <PlayIcon className="h-4 w-4" />
                  )}
                </button>

                <PlaybackRateMenu
                  playbackRate={player.playbackRate}
                  tempPlaybackRate={player.tempPlaybackRate}
                  setRate={player.setRate}
                />

                <VolumeControl
                  muted={player.muted}
                  volume={player.volume}
                  setMuted={player.setMuted}
                  setVolume={player.setVolume}
                />
              </div>

              <TimeDisplay
                time={player.time}
                duration={player.duration}
                fps={getFrameRate()}
                isCompactVideoControls={isCompactVideoControls}
                timeFormat={timeFormat}
                setTimeFormat={setPreferredTimeFormat}
                onSeek={player.seek}
              />

              <div className="flex items-center justify-end gap-x-3">
                {canSwitchQuality && (
                  <select
                    value={currentQuality}
                    onChange={(e) =>
                      handleQualityChange(e.target.value as "1080p" | "Original")
                    }
                    title="Quality"
                    className="bg-transparent text-xs text-[#282A30] outline-none"
                  >
                    <option value="1080p" className="bg-white">
                      1080p
                    </option>
                    <option value="Original" className="bg-white">
                      Original
                    </option>
                  </select>
                )}

                {transcription != null && (
                  <button
                    type="button"
                    title={
                      transcriptPanelOpen
                        ? "Hide transcript"
                        : "Show transcript"
                    }
                    onClick={() =>
                      setTranscriptPanelOpen(!transcriptPanelOpen)
                    }
                    className={classNames(
                      "flex h-6 w-6 items-center justify-center rounded-md transition-colors",
                      transcriptPanelOpen
                        ? "bg-[#F4F3FF] text-[#6526DB] hover:bg-[#ECEAFE]"
                        : "text-[#71717A] hover:bg-[#F7F8F9] hover:text-[#282A30]",
                    )}
                  >
                    <DocumentTextIcon className="h-3.5 w-3.5" />
                  </button>
                )}

                <span className="relative flex h-6 w-6 items-center justify-center">
                  <SettingsPopover>
                    <Cog6ToothIcon className="h-3.5 w-3.5" />
                  </SettingsPopover>
                  <QualityBadge
                    videoHeight={player.videoHeight}
                    className="absolute -top-[12px] left-3.5"
                  />
                </span>

                {!disableFullScreen && (
                  <span
                    title={isFullScreen ? "Exit fullscreen" : "Fullscreen"}
                    onClick={toggleFullScreen}
                    className="flex h-6 w-6 cursor-pointer items-center justify-center text-[#282A30] transition-colors hover:text-black"
                  >
                    {isFullScreen ? (
                      <ArrowsPointingInIcon className="h-3.5 w-3.5" />
                    ) : (
                      <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;

